import { useState, type DragEvent } from 'react'
import { GripVertical } from 'lucide-react'

// A list reordered by dragging a row's handle, or with the arrow keys on it,
// as Trello and GitHub do. `move` takes a row from one place to another.
export function useDragOrder(count: number, move: (from: number, to: number) => void, disabled = false) {
  const [dragged, setDragged] = useState<number | null>(null)
  const [over, setOver] = useState<number | null>(null)

  // A row takes another dropped on it.
  const dropTarget = (index: number) => ({
    'data-drag-row': true,
    className: [dragged === index && 'is-dragging', over === index && dragged !== index && 'is-over'].filter(Boolean).join(' ') || undefined,
    onDragOver: (event: DragEvent) => {
      if (dragged === null) return
      event.preventDefault()
      setOver(index)
    },
    onDrop: (event: DragEvent) => {
      event.preventDefault()
      if (dragged !== null && dragged !== index) move(dragged, index)
      setDragged(null)
      setOver(null)
    },
  })

  const handle = (index: number, label: string) => (
    <button type="button" className="drag-handle" draggable disabled={disabled} aria-label={`Arrastar ${label}`}
      title="Arraste para mudar a ordem, ou use as setas do teclado"
      onDragStart={(event) => {
        setDragged(index)
        event.dataTransfer.effectAllowed = 'move'
        event.dataTransfer.setData('text/plain', label)
        const row = event.currentTarget.closest('[data-drag-row]')
        if (row) event.dataTransfer.setDragImage(row, 20, 20)
      }}
      onDragEnd={() => { setDragged(null); setOver(null) }}
      onKeyDown={(event) => {
        const to = event.key === 'ArrowUp' ? index - 1 : event.key === 'ArrowDown' ? index + 1 : -1
        if (to < 0 || to >= count) return
        event.preventDefault()
        move(index, to)
      }}>
      <GripVertical size={16} />
    </button>
  )

  return { dropTarget, handle }
}
